import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import NotFoundPage from './pages/NotFoundPage';

import AdminDashboard from './pages/Admin/AdminDashboard';
import Users from './pages/Admin/sections/Users';
import Overview from './pages/dashboard/Overview';
import MyProfile from './pages/dashboard/MyProfile';
import Settings from './pages/dashboard/Settings';

import AdminAdmissions from './pages/Admin/sections/Admissions/AdminAdmissions';
import AdminScholarships from './pages/Admin/sections/Scholarships/AdminScholarships';
import AdminMagazines from './pages/Admin/sections/Magazines';
import AdminNotices from './pages/Admin/sections/Notices/AdminNotices';
import AdminGallery from './pages/Admin/sections/Gallery/AdminGallery';

import DisclaimerPopup from './components/HomePage/DisclaimerPopup';
import Navbar from './components/Navbar/Navbar';
import Hero from './components/HomePage/Hero';
import Cards from './components/HomePage/Cards';
import Courses from './components/HomePage/Courses';
import Gallery from './components/HomePage/Gallery';
import Placements from './components/HomePage/Placements';
import Testimonials from './components/HomePage/Testimonials';
import Footer from './components/Footer/Footer';
import Chatbot from './components/Chatbot/Chatbot';

import AuthPage from './components/AuthPage/AuthPage';
import AboutUs from './components/AboutUs/AboutUs';

// ── Academics
import UGCourses from './components/Academics/UGCourses';
import CertificateCourses from './components/Academics/CertificateCourses';

// ── Departments
import ArtsDepartment from './components/Departments/ArtsDepartment';
import ScienceDepartment from './components/Departments/ScienceDepartment';
import ComputerScienceDepartment from './components/Departments/ComputerScienceDepartment';

// ── IQAC
import IQUACCommittee from './components/IQAC/IQAC-Committee';
import IQUACAQARReport from './components/IQAC/IQAC-AQARReports';
import IQUACMeetingMinutes from './components/IQAC/IQAC-MeetingMinutes';

// ── Admissions
import AdmissionProcess from './components/Admissions/AdmissionProcess';
import FirstYearAdmission from './components/Admissions/FirstYearAdmission';
import SecondYearAdmission from './components/Admissions/SecondYearAdmission';
import ThirdYearAdmission from './components/Admissions/ThirdYearAdmission';
import Scholarships from './components/Admissions/Scholarships';

// ── Examinations
import ExamNotices from './components/Examinations/ExamNotices';
import ExamResults from './components/Examinations/ExamResults';
import ExamTimetables from './components/Examinations/ExamTimetables';

// ── Students Corner
import StudentCornerNSS from './components/StudentsCorner/StudentCorner-NSS';
import Magazine from './components/StudentsCorner/Magazine';
import Alumni from './components/StudentsCorner/Alumni';

// ── RTI & Feedback
import RTIPage from './components/RTIPage/RTIPage';
import FeedbackPage from './components/FeedbackPage/FeedbackPage';

// ── Phase 1 — About Sub-pages
import AboutSanstha from './pages/about/AboutSanstha';
import AboutCollege from './pages/about/AboutCollege';
import VisionMission from './pages/about/VisionMission';

// ── Phase 1 — Management
import GoverningCouncil from './pages/management/GoverningCouncil';
import CollegeDevelopmentCommittee from './pages/management/CollegeDevelopmentCommittee';

// ── Phase 1 — Academics extras
import LibraryPage from './pages/academics/LibraryPage';
import PhysicalEducation from './pages/academics/PhysicalEducation';
import ProgrammeOutcome from './pages/academics/ProgrammeOutcome';
import EnvironmentAwareness from './pages/academics/EnvironmentAwareness';

// ── Phase 1 — Students Corner extras
import EligibilityNumber from './pages/students/EligibilityNumber';
import AdmissionPolicy from './pages/students/AdmissionPolicy';
import {
  EnrollmentList, OldQuestionPapers, GovernmentScholarship, UniversityScholarship,
  BoardStudentDevelopment, AcademicCalendar, StudyMaterial, CasteDiscrimination, PowerPointBank
} from './pages/students/StudentCornerPages';
import GrievanceCell from './pages/students/GrievanceCell';

// ── Phase 1 — Committees
import CommitteesPage from './pages/committees/CommitteesPage';

// ── Phase 1 — IQAC extras
import {
  IQACSSR, IQACCertification, IQACBestPractices,
  IQACSeminars, IQACExtensionActivity, IQACCapacityBuilding
} from './pages/iqac/IQACPages';

// ── Phase 1 — Examination extras
import ExamCommittee from './pages/examinations/ExamCommittee';

// ── Phase 1 — Feedback system
import FeedbackForm from './pages/feedback/FeedbackForm';
import FeedbackReport from './pages/feedback/FeedbackReport';
import ActionTakenReport from './pages/feedback/ActionTakenReport';

// ── Phase 1 — ICT & others
import ICTFacilities from './pages/ict/ICTFacilities';
import TeacherList from './pages/ict/TeacherList';
import NewsAndEvents from './pages/NewsAndEvents';
import ContactUs from './pages/ContactUs';
import SSSPage from './pages/SSSPage';
import MOUPage from './pages/MOUPage';
import LinkagePage from './pages/LinkagePage';
import FAQPage from './pages/FAQPage';

// ── Phase 2 — Events & Polls
import EventsPage from './pages/events/EventsPage';
import PollVotePage from './pages/events/PollVotePage';
import AdminPolls from './pages/Admin/sections/Polls/AdminPolls';
import PollResults from './pages/Admin/sections/Polls/PollResults';

import './styles/App.css';

function HomePage() {
  return (
    <>
      <DisclaimerPopup />
      <Navbar />
      <Hero />
      <Cards />
      <Courses />
      <Gallery />
      <Placements />
      <Testimonials />
      <Footer />
      <Chatbot />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        {/* Global toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: { background: '#1e2a38', color: '#fff', borderRadius: '10px' },
            success: { iconTheme: { primary: '#43e97b', secondary: '#fff' } },
            error: { iconTheme: { primary: '#ff6b6b', secondary: '#fff' } },
          }}
        />
        <Routes>
          {/* ── PUBLIC ── */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<AuthPage />} />

          {/* ── PROTECTED ADMIN ── */}
          <Route path="/admin-dashboard" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <Users />
            </ProtectedRoute>
          } />
          <Route path="/admin/admissions" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <AdminAdmissions />
            </ProtectedRoute>
          } />
          <Route path="/admin/scholarships" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <AdminScholarships />
            </ProtectedRoute>
          } />
          <Route path="/admin/magazines" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <AdminMagazines />
            </ProtectedRoute>
          } />
          <Route path="/admin/notices" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <AdminNotices />
            </ProtectedRoute>
          } />
          <Route path="/admin/gallery" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <AdminGallery />
            </ProtectedRoute>
          } />

          {/* ── PROTECTED USER DASHBOARD ── */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Overview />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <MyProfile />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />

          {/* ── ABOUT ── */}
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/about-sanstha" element={<AboutSanstha />} />
          <Route path="/about-college" element={<AboutCollege />} />
          <Route path="/vision-mission" element={<VisionMission />} />

          {/* ── MANAGEMENT ── */}
          <Route path="/governing-council" element={<GoverningCouncil />} />
          <Route path="/college-development-committee" element={<CollegeDevelopmentCommittee />} />

          {/* ── ACADEMICS ── */}
          <Route path="/ugcourses" element={<UGCourses />} />
          <Route path="/certificate-courses" element={<CertificateCourses />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/physical-education" element={<PhysicalEducation />} />
          <Route path="/programme-outcome" element={<ProgrammeOutcome />} />
          <Route path="/environment-awareness" element={<EnvironmentAwareness />} />

          {/* ── DEPARTMENTS ── */}
          <Route path="/arts-department" element={<ArtsDepartment />} />
          <Route path="/science-department" element={<ScienceDepartment />} />
          <Route path="/computer-science-department" element={<ComputerScienceDepartment />} />

          {/* ── IQAC ── */}
          <Route path="/iqac-committee" element={<IQUACCommittee />} />
          <Route path="/iqac-aqar-reports" element={<IQUACAQARReport />} />
          <Route path="/iqac-meeting-minutes" element={<IQUACMeetingMinutes />} />
          <Route path="/iqac-ssr" element={<IQACSSR />} />
          <Route path="/iqac-certification" element={<IQACCertification />} />
          <Route path="/iqac-best-practices" element={<IQACBestPractices />} />
          <Route path="/iqac-seminars" element={<IQACSeminars />} />
          <Route path="/iqac-extension-activity" element={<IQACExtensionActivity />} />
          <Route path="/iqac-capacity-building" element={<IQACCapacityBuilding />} />

          {/* ── ADMISSIONS ── */}
          <Route path="/admission-process" element={<AdmissionProcess />} />
          <Route path="/first-year-admission" element={<FirstYearAdmission />} />
          <Route path="/second-year-admission" element={<SecondYearAdmission />} />
          <Route path="/third-year-admission" element={<ThirdYearAdmission />} />
          <Route path="/scholarships" element={<Scholarships />} />

          {/* ── EXAMINATIONS ── */}
          <Route path="/exam-notices" element={<ExamNotices />} />
          <Route path="/exam-results" element={<ExamResults />} />
          <Route path="/exam-timetables" element={<ExamTimetables />} />
          <Route path="/exam-committee" element={<ExamCommittee />} />

          {/* ── STUDENTS CORNER ── */}
          <Route path="/student-corner-nss" element={<StudentCornerNSS />} />
          <Route path="/magazine" element={<Magazine />} />
          <Route path="/alumni" element={<Alumni />} />
          <Route path="/eligibility-number" element={<EligibilityNumber />} />
          <Route path="/admission-policy" element={<AdmissionPolicy />} />
          <Route path="/enrollment-list" element={<EnrollmentList />} />
          <Route path="/old-question-papers" element={<OldQuestionPapers />} />
          <Route path="/government-scholarship" element={<GovernmentScholarship />} />
          <Route path="/university-scholarship" element={<UniversityScholarship />} />
          <Route path="/board-student-development" element={<BoardStudentDevelopment />} />
          <Route path="/academic-calendar" element={<AcademicCalendar />} />
          <Route path="/study-material" element={<StudyMaterial />} />
          <Route path="/grievance-cell" element={<GrievanceCell />} />
          <Route path="/caste-discrimination" element={<CasteDiscrimination />} />
          <Route path="/powerpoint-bank" element={<PowerPointBank />} />

          {/* ── COMMITTEES ── */}
          <Route path="/committees" element={<CommitteesPage />} />

          {/* ── FEEDBACK ── */}
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/feedback/library" element={<FeedbackForm type="library" />} />
          <Route path="/feedback/alumni" element={<FeedbackForm type="alumni" />} />
          <Route path="/feedback/employer" element={<FeedbackForm type="employer" />} />
          <Route path="/feedback/parents" element={<FeedbackForm type="parents" />} />
          <Route path="/feedback/student" element={<FeedbackForm type="student" />} />
          <Route path="/feedback/teacher" element={<FeedbackForm type="teacher" />} />
          <Route path="/feedback/report" element={<FeedbackReport />} />
          <Route path="/feedback/action-taken" element={<ActionTakenReport />} />

          {/* ── ICT FACILITIES ── */}
          <Route path="/ict-facilities" element={<ICTFacilities />} />
          <Route path="/teacher-list" element={<TeacherList />} />

          {/* ── OTHER PAGES ── */}
          <Route path="/rti" element={<RTIPage />} />
          <Route path="/news-and-events" element={<NewsAndEvents />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/sss" element={<SSSPage />} />
          <Route path="/mou" element={<MOUPage />} />
          <Route path="/linkage" element={<LinkagePage />} />
          <Route path="/faq" element={<FAQPage />} />

          {/* ── EVENTS & POLLS ── */}
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/poll/:id" element={<PollVotePage />} />

          {/* ── ADMIN POLLS ── */}
          <Route path="/admin/polls" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <AdminPolls />
            </ProtectedRoute>
          } />
          <Route path="/admin/polls/:id/results" element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <PollResults />
            </ProtectedRoute>
          } />

          {/* ── 404 CATCH-ALL ── */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
